import {
  Button,
  ButtonWithPopOut,
  useButtonWithPopOut,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { capitalize } from "@s-libs/micro-dash";
import { useEffect, useRef, useState, useCallback } from "react";

import {
  buttonContainerCss,
  buttonWithPopoutCss,
  getSortByButtonOptionCss,
  popoutContainerCss,
  selectedOptionIndicatorCss,
} from "@/search/Langston/ContentTypeFilter/ContentHeaderSortByButton/ContentHeaderSortByButton.styles";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";

import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";

const sortByFilterOptions = LangstonParameterSchema.sort.values;
const defaultSortByOption = LangstonParameterSchema.sort.default;

const ButtonWithTrackEvent = withTrackEvent(Button);

const ContentHeaderSortByButton = () => {
  const {
    handlers: {
      getSearchParamValues,
      setSearchParamValue,
      hasSearchParamValue,
    },
  } = useSearchPageContext();

  const buttonRef = useRef();
  const controllingProps = useButtonWithPopOut({ buttonRef });
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const menuItemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const popoutRef = useRef<HTMLDivElement>(null);

  const selectedSortByValue =
    getSearchParamValues(LangstonParameterSchema.sort.key)?.[0] ||
    defaultSortByOption;
  const selectedSortByLabel = sortByFilterOptions.find(
    (option) => option.value === selectedSortByValue,
  )?.label;
  const capitalizedLabel =
    selectedSortByLabel &&
    selectedSortByLabel
      .split(" ")
      .map((word) => capitalize(word))
      .join(" ");

  const handleOptionClick = (value: string) => {
    setSearchParamValue(LangstonParameterSchema.sort.key, value);
    setFocusedIndex(-1);
    const button = document.getElementById("search-sort-by-header-button");
    button?.focus();
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const itemCount = sortByFilterOptions.length;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const nextIndex = (focusedIndex + 1) % itemCount;
          setFocusedIndex(nextIndex);
          menuItemsRef.current[nextIndex]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const prevIndex =
            focusedIndex === 0 ? itemCount - 1 : focusedIndex - 1;
          setFocusedIndex(prevIndex);
          menuItemsRef.current[prevIndex]?.focus();
          break;
        }
        case "Escape": {
          e.preventDefault();
          setFocusedIndex(-1);
          const button = document.getElementById(
            "search-sort-by-header-button",
          );
          button?.focus();
          break;
        }
        case " ":
        case "Enter": {
          e.preventDefault();
          const currentOption = sortByFilterOptions[focusedIndex];
          if (currentOption) {
            handleOptionClick(currentOption.value);
            controllingProps.setIsPopOutVisible(false);
          }
          break;
        }
        default:
          break;
      }
    },
    [focusedIndex],
  );

  useEffect(() => {
    if (controllingProps.isPopOutVisible) {
      setFocusedIndex(0);
      setTimeout(() => {
        menuItemsRef.current[0]?.focus();
      }, 0);
    }
  }, [controllingProps.isPopOutVisible]);

  return (
    <ButtonWithPopOut
      id="search-sort-by-header-button"
      closeOnBlur
      __cssFor={buttonWithPopoutCss}
      horizontalOffset="0px"
      verticalOffset="6px"
      buttonContents={`Sort by ${capitalizedLabel}`}
      ref={buttonRef}
      ariaProps={{ "aria-haspopup": "menu" }}
      {...controllingProps}
      popOutContents={
        <div
          ref={popoutRef}
          css={popoutContainerCss}
          role="menu"
          aria-label="Sort by options"
        >
          {sortByFilterOptions.map((option, index) => {
            const isSelected = hasSearchParamValue(
              LangstonParameterSchema.sort.key,
              option.value,
            );

            return (
              <div key={option.value} css={buttonContainerCss} role="none">
                {isSelected && <div css={selectedOptionIndicatorCss} />}

                <ButtonWithTrackEvent
                  ref={(el) => {
                    if (el) {
                      const buttonElement =
                        (el as any)?.querySelector?.("button") || el;
                      menuItemsRef.current[index] =
                        buttonElement as HTMLButtonElement;
                    }
                  }}
                  role="menuitemradio"
                  dataProps={{
                    "data-qa-selector": `search-${LangstonParameterSchema.sort.key}-filter-option`,
                    "aria-checked": isSelected,
                  }}
                  objectType={objectTypes.content}
                  actionName={actionNames.filter}
                  flavor={Button.flavors.incognito}
                  onClick={() => handleOptionClick(option.value)}
                  onKeyDown={handleKeyDown}
                  __cssFor={getSortByButtonOptionCss(isSelected)}
                >
                  {option.label}
                </ButtonWithTrackEvent>
              </div>
            );
          })}
        </div>
      }
    />
  );
};

ContentHeaderSortByButton.displayName = "ContentHeaderSortByButton";
export { ContentHeaderSortByButton };
